import {
  Menu as MuiMenu,
  MenuProps as MuiMenuProps,
  useTheme,
  Box,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import React from "react";
import { useDarkMode } from "../../hooks/useDarkMode";

export interface MenuItemProps {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  color?: string;
  hoverBackgroundColor?: string;
  customContent?: React.ReactNode;
}

interface MenuProps extends Omit<MuiMenuProps, "slotProps" | "children"> {
  children?: React.ReactNode;
  items?: MenuItemProps[];
  width?: string | number;
  padding?: string | number;
  marginTop?: string | number;
}

export const MenuItem: React.FC<
  MenuItemProps & { palette: any; isDarkMode: boolean }
> = ({
  label,
  icon,
  onClick,
  disabled = false,
  color,
  hoverBackgroundColor,
  customContent,
  palette,
  isDarkMode,
}) => {
  const defaultColor = isDarkMode ? palette.gray[300] : palette.gray[600];
  const defaultHoverBg = isDarkMode ? palette.gray[800] : palette.gray[200];
  const textColor = color || defaultColor;
  const hoverBg = hoverBackgroundColor || defaultHoverBg;

  return (
    <ListItemButton
      onClick={onClick}
      disabled={disabled}
      sx={{
        display: "flex",
        padding: "4px 8px",
        alignItems: "center",
        gap: "8px",
        alignSelf: "stretch",
        borderRadius: "4px",
        opacity: disabled ? 0.5 : 1,
        "&:hover": {
          backgroundColor: hoverBg,
        },
      }}
    >
      {icon && (
        <ListItemIcon
          sx={{
            minWidth: "auto",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {icon}
        </ListItemIcon>
      )}
      <ListItemText
        primary={label}
        primaryTypographyProps={{
          sx: {
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 1,
            flex: "1 0 0",
            overflow: "hidden",
            color: textColor,
            fontFeatureSettings: "'liga' off, 'clig' off",
            textOverflow: "ellipsis",
            fontFamily: "Roboto",
            fontSize: "16px",
            fontStyle: "normal",
            fontWeight: 400,
            lineHeight: "150%",
            letterSpacing: "0.15px",
            margin: 0,
          },
        }}
      />
      {customContent}
    </ListItemButton>
  );
};

export const Menu: React.FC<MenuProps> = ({
  children,
  items,
  width = "200px",
  marginTop = "8px",
  anchorOrigin = {
    vertical: "bottom",
    horizontal: "right",
  },
  transformOrigin = {
    vertical: "top",
    horizontal: "right",
  },
  ...menuProps
}) => {
  const theme = useTheme();
  const { customColorPalette: palette } = theme;
  const isDarkMode = useDarkMode();

  return (
    <MuiMenu
      {...menuProps}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      slotProps={{
        paper: {
          sx: {
            display: "flex",
            width,
            paddingX: "8px",
            flexDirection: "column",
            justifyContent: "center",
            gap: "16px",
            borderRadius: "8px",
            border: `1px solid ${
              isDarkMode ? palette.gray[700] : palette.gray[300]
            }`,
            backgroundColor: isDarkMode ? palette.gray[900] : palette.gray.A100,
            marginTop,
            boxShadow: "none",
          },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          width: "100%",
        }}
      >
        {items
          ? items.map((item, index) => (
              <MenuItem
                key={index}
                {...item}
                palette={palette}
                isDarkMode={isDarkMode}
              />
            ))
          : children}
      </Box>
    </MuiMenu>
  );
};
